import React, { useEffect, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";

function RankRow({ item, index, total, onMove, qid }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item.id} dragListener={false} dragControls={controls} className="st-panel-2 flex items-center gap-3 px-3 py-3 select-none" data-testid={`q-${qid}-rank-${item.id}`}
      whileDrag={{ scale: 1.02, boxShadow: "0 20px 40px -20px rgba(0,0,0,.9)", borderColor: "rgba(200,247,74,.5)" }}>
      <span className="st-drag p-1 st-faint" onPointerDown={(e) => controls.start(e)} data-testid={`q-${qid}-rank-${item.id}-handle`}><GripVertical size={18} /></span>
      <span className="grid place-items-center w-8 h-8 rounded-lg font-extrabold text-sm shrink-0" style={{ background: index === 0 ? "var(--st-grad)" : "rgba(243,239,230,.07)", color: index === 0 ? "#0B0E13" : "var(--st-text)" }}>{index + 1}</span>
      <span className="font-semibold text-[15px] flex-1 leading-snug">{item.label}</span>
      <span className="flex flex-col">
        <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} className="st-btn st-btn-quiet !p-1" data-testid={`q-${qid}-rank-${item.id}-up`}><ChevronUp size={16} /></button>
        <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1} className="st-btn st-btn-quiet !p-1" data-testid={`q-${qid}-rank-${item.id}-down`}><ChevronDown size={16} /></button>
      </span>
    </Reorder.Item>
  );
}

export function RankList({ q, value, onChange }) {
  const [order, setOrder] = useState(value || q.items.map((i) => i.id));
  useEffect(() => { if (value) setOrder(value); }, [value]);
  const byId = Object.fromEntries(q.items.map((i) => [i.id, i]));
  const commit = (next) => { setOrder(next); onChange(next); };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };
  return (
    <div>
      <p className="st-caps mb-3">Drag to reorder · most important at the top</p>
      <Reorder.Group axis="y" values={order} onReorder={commit} className="space-y-2" data-testid={`q-${q.id}-rank`}>
        {order.map((id, i) => <RankRow key={id} item={byId[id]} index={i} total={order.length} onMove={move} qid={q.id} />)}
      </Reorder.Group>
      {!value && <button type="button" className="st-btn st-btn-ghost mt-3 text-xs" onClick={() => commit(order)} data-testid={`q-${q.id}-rank-confirm`}>Confirm this order</button>}
    </div>
  );
}

export function Allocate({ q, value, onChange }) {
  const v = value || {};
  const total = q.items.reduce((s, i) => s + (Number(v[i.id]) || 0), 0);
  const remaining = q.total - total;
  const ok = Math.abs(remaining) < 0.5;
  const fmt = (n) => (q.unit === "£" ? `£${Math.round(n).toLocaleString("en-GB")}` : `${Math.round(n)}%`);
  const step = q.unit === "£" ? Math.max(500, Math.round(q.total / 40 / 500) * 500) : 5;
  const set = (id, n) => onChange({ ...v, [id]: Math.max(0, Math.min(q.total, Number(n) || 0)) });
  const even = () => {
    const share = Math.floor(q.total / q.items.length / step) * step;
    const next = Object.fromEntries(q.items.map((i) => [i.id, share]));
    next[q.items[0].id] += q.total - share * q.items.length;
    onChange(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4 st-panel-2 px-4 py-3" data-testid={`q-${q.id}-total`}>
        <div>
          <p className="st-caps">Allocated</p>
          <p className="text-xl font-extrabold" style={{ color: ok ? "var(--st-lime)" : "var(--st-amber)" }}>{fmt(total)} <span className="st-faint text-sm font-semibold">/ {fmt(q.total)}</span></p>
        </div>
        <div className="text-right">
          <p className="st-caps">{remaining >= 0 ? "Remaining" : "Over by"}</p>
          <p className="text-xl font-extrabold" style={{ color: ok ? "var(--st-lime)" : "var(--st-red)" }} data-testid={`q-${q.id}-remaining`}>{fmt(Math.abs(remaining))}</p>
        </div>
        <button type="button" onClick={even} className="st-btn st-btn-ghost text-xs" data-testid={`q-${q.id}-even`}>Split evenly</button>
      </div>
      <div className="space-y-3">
        {q.items.map((i) => {
          const n = Number(v[i.id]) || 0;
          return (
            <div key={i.id} className="st-panel-2 p-4" data-testid={`q-${q.id}-alloc-${i.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-semibold text-[15px] leading-snug">{i.label}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" onClick={() => set(i.id, n - step)} className="st-btn st-btn-ghost !px-2.5 !py-1 text-sm" data-testid={`q-${q.id}-alloc-${i.id}-minus`}>−</button>
                  <input type="number" value={n} onChange={(e) => set(i.id, e.target.value)} className="st-input !w-28 !py-1.5 text-right font-bold" data-testid={`q-${q.id}-alloc-${i.id}-input`} />
                  <button type="button" onClick={() => set(i.id, n + step)} className="st-btn st-btn-ghost !px-2.5 !py-1 text-sm" data-testid={`q-${q.id}-alloc-${i.id}-plus`}>+</button>
                </div>
              </div>
              <input type="range" min={0} max={q.total} step={step} value={n} onChange={(e) => set(i.id, e.target.value)} className="st-range" />
              <div className="flex justify-between st-faint text-xs mt-1"><span>{fmt(n)}</span><span>{q.total ? Math.round((n / q.total) * 100) : 0}% of total</span></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
